import merge from 'lodash.merge';
import get from 'lodash.get';
import isPlainObject from 'lodash.isplainobject';
import axios from 'axios';
import JwtBaseClient from './JwtBaseClient';
import ApiError from '../models/ApiError';
/**
 * @class EasyPassClient
 * @extends JwtBaseClient
 * @description Client used to interface with Easy Pass Gateway and BFF.
 */
class EasyPassClient extends JwtBaseClient {
  constructor(user, baseUri, additionalHeaders) {
    const pathToApiErrors = 'response.data.errors';
    const uri = baseUri || import.meta.env.VITE_EASY_PASS_BASE_URI;
    const headers = {
      'acv-client-version': import.meta.env.VITE_VERSION,
      'acv-client-type': 'desktop',
      'acv-client-os': 'web',
      'acv-client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...additionalHeaders,
    };
    super(uri, pathToApiErrors, headers);
    this.user = user;
  }

  request(config) {
    const configWithHeaders = config;
    configWithHeaders.headers = merge(this.headers, config.headers);
    configWithHeaders.data = this.addDefaultDealerId(config, 'data');
    // No need to test for GET, as axios will strip query params for any non-GET methods - etc
    configWithHeaders.params = this.addDefaultDealerId(config, 'params');

    return this.axiosInstance.request(configWithHeaders)
      .catch((error) => {
        if (axios.isCancel(error) || error.message === 'canceled') {
          return Promise.reject(error);
        }
        const defaultErrorMessage = 'There was problem processing your request. Please try again or adjust your parameters.';

        let errorMessage = get(
          error,
          this.pathToApiErrors,
          [{
            detail: defaultErrorMessage,
          }],
        );

        if (!errorMessage) {
          // found path but no information, default message (so usages can still infer in catches)
          errorMessage = [{ detail: defaultErrorMessage }];
        }

        const errors = errorMessage.map((e) => new ApiError(e.detail, { ...e, ...error }));
        return Promise.reject(errors);
      });
  }

  addDefaultDealerId(data, dataKey) {
    const useCamelCaseHeader = get(data, 'headers[\'json-naming-strategy\']') === 'camel';
    const dealerIdKey = useCamelCaseHeader
      ? 'dealerId'
      : 'dealer_id';
    const { hasMultipleDealerships, defaultDealerId } = this.user;
    if (!hasMultipleDealerships
        && data[dataKey]
        && isPlainObject(data[dataKey])
        && !Object.keys(data[dataKey]).includes(dealerIdKey)) {
      const copiedData = {
        ...data[dataKey],
      };
      return {
        ...copiedData,
        dealerId: defaultDealerId || null,
      };
    }
    return data[dataKey];
  }
}

export default EasyPassClient;
